import AuthFooter from '../registration/AuthFooter';
import {FormEvent, useState} from 'react';
import {Alert, Box, Button, CircularProgress, Container, Paper, Stack, TextField, Typography} from '@mui/material';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import {alpha} from '@mui/material/styles';
import {Link as RouterLink, useLocation} from 'react-router-dom';
import {localizedLandingUrl} from '../../common/landingUrl';
import {authAPI} from '../../services/AuthServices';
import {useAppTranslation} from '@mybake/ui/hooks/useAppTranslation';
import {getApiErrorMessage, getApiFieldErrors} from '../../common/apiErrors';
import LanguageSwitcher from '@mybake/ui/components/header/LanguageSwitcher';

const Login = () => {
    const {t, i18n} = useAppTranslation();
    const location = useLocation();
    const loginState = location.state as {email?: string; accountCreated?: boolean} | null;
    const [email, setEmail] = useState(loginState?.email || '');
    const [password, setPassword] = useState('');
    const [emailTouched, setEmailTouched] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [logIn, {error, isLoading, reset}] = authAPI.useLogInMutation();
    const normalizedEmail = email.trim();
    const emailError = !normalizedEmail ? t('apiErrors:validation.required')
        : !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail) ? t('auth:email_invalid') : '';
    const fieldErrors = Object.fromEntries(Object.entries(error ? getApiFieldErrors(error, t) : {}).map(([key, value]) => [key.toLowerCase(), value]));
    const shownEmailError = ((emailTouched || submitted) && emailError) || fieldErrors.email || '';
    const passwordError = (submitted && !password ? t('apiErrors:validation.required') : '') || fieldErrors.password || '';

    const submit = (event: FormEvent) => {
        event.preventDefault();
        if (isLoading) return;
        setSubmitted(true);
        reset();
        if (emailError || !password) return;
        // Authentication and the return route remain managed by App's auth context.
        void logIn([normalizedEmail, password]);
    };

    return <Box sx={{minHeight: '100vh', bgcolor: 'background.default', py: {xs: 2, md: 6}, display: 'flex', alignItems: 'center'}}>
        <Container maxWidth="sm">
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{mb: 3}}>
                <Button component="a" href={localizedLandingUrl(i18n.resolvedLanguage || i18n.language)} startIcon={<ArrowBackRoundedIcon/>}>{t('registration:back')}</Button>
                <LanguageSwitcher/>
            </Stack>
            <Paper elevation={0} sx={{p: {xs: 3, sm: 5}, border: 1, borderColor: 'divider', borderRadius: 4,
                '& .MuiInputBase-root, & input, & textarea, & [contenteditable="true"]': {cursor: 'default'}}}>
                <Stack spacing={3}>
                    <Box>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{mb: 3}}>
                            <Box component="img" src={`${process.env.PUBLIC_URL}/branding/mybake-wordmark-3200px.png`} alt="MyBake.pro" width={3200} height={712} sx={{display: 'block', width: 160, maxWidth: '100%', height: 'auto'}}/>
                            <Box sx={{display: 'grid', placeItems: 'center', width: 48, height: 48, borderRadius: 3, color: 'primary.main', bgcolor: theme => alpha(theme.palette.primary.main, 0.08)}}><LockRoundedIcon/></Box>
                        </Stack>
                        <Typography component="h1" variant="h2" sx={{mb: 1}}>{t('auth:login_title')}</Typography>
                        <Typography color="text.secondary">{t('auth:login_subtitle')}</Typography>
                    </Box>
                    {loginState?.accountCreated && <Alert severity="success">{t('registration:account_created_body')}</Alert>}
                    {!!error && <Alert severity="error" sx={{overflowWrap: 'anywhere'}}>{getApiErrorMessage(error, t)}</Alert>}
                    <Box component="form" noValidate onSubmit={submit} aria-busy={isLoading}>
                        <Stack spacing={2.5}>
                            <TextField id="login-email" name="email" label={t('auth:email_holder')} type="email" autoComplete="username" fullWidth required disabled={isLoading}
                                value={email} onChange={event => {setEmail(event.target.value); reset();}} onBlur={() => setEmailTouched(true)}
                                error={!!shownEmailError} helperText={shownEmailError || undefined}/>
                            <TextField id="login-password" name="password" label={t('auth:psw_holder')} type="password" autoComplete="current-password" fullWidth required disabled={isLoading}
                                value={password} onChange={event => {setPassword(event.target.value); reset();}}
                                error={!!passwordError} helperText={passwordError || undefined}/>
                            <Button type="submit" variant="contained" fullWidth disabled={isLoading} sx={{minHeight: 52}}
                                startIcon={isLoading ? <CircularProgress size={18} color="inherit"/> : undefined}>{t('auth:login_btn')}</Button>
                            <Button component={RouterLink} to="/forgot-password">{t('auth:recovery.forgot_link')}</Button>
                        </Stack>
                    </Box>
                </Stack>
            </Paper>
            <AuthFooter/>
        </Container>
    </Box>;
};

export default Login;
